/**
 * The form's step bar (Q1, 0.9.40, on the donation form design): "Step 1 of 3" and the total on one line, then a bar per
 * step, gold up to and including the one the donor is on, each with its name under it ("1 Your gift", "2 Your details",
 * "3 Payment"); a step already done shows a check. It carries real information: the classes and `aria-current` follow the
 * true state, and the change is announced through a live region (WCAG: a step change is not visual only).
 *
 * The wizard keeps two steps of its own (the gift, then the details with the payment under them); the payment fields
 * appearing is the third step the donor sees ({@see Wizard#displayStep}).
 */

/**
 * @param {string} text
 * @return {string}
 */
function __( text ) {
	return window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text;
}

/** The names of the three steps, in order. */
const STEP_NAMES = [ 'Your gift', 'Your details', 'Payment' ];

/**
 * Builds the bar. The total is filled in later ({@see setStepTotal}).
 *
 * @return {HTMLElement}
 */
export function buildStitch() {
	const nav = document.createElement( 'nav' );

	nav.className = 'anudan-checkout__progress';
	nav.setAttribute( 'aria-label', __( 'Donation steps' ) );

	const top = document.createElement( 'div' );
	const count = document.createElement( 'span' );
	const total = document.createElement( 'span' );

	top.className = 'anudan-checkout__progress-top';
	count.className = 'anudan-checkout__progress-count';
	count.setAttribute( 'data-anudan-progress-count', '' );
	total.className = 'anudan-checkout__progress-total';
	total.setAttribute( 'data-anudan-progress-total', '' );
	total.hidden = true;
	top.appendChild( count );
	top.appendChild( total );

	const list = document.createElement( 'ol' );

	list.className = 'anudan-checkout__progress-steps';

	STEP_NAMES.forEach( ( name, index ) => {
		const item = document.createElement( 'li' );
		const bar = document.createElement( 'span' );
		const label = document.createElement( 'span' );
		const mark = document.createElement( 'span' );

		item.className = 'anudan-checkout__progress-step';
		item.setAttribute( 'data-anudan-stitch-segment', String( index ) );
		bar.className = 'anudan-checkout__progress-bar';
		bar.setAttribute( 'aria-hidden', 'true' );
		label.className = 'anudan-checkout__progress-label';
		mark.className = 'anudan-checkout__progress-mark';
		mark.setAttribute( 'aria-hidden', 'true' );
		mark.textContent = String( index + 1 );
		label.appendChild( mark );
		label.appendChild( document.createTextNode( __( name ) ) );
		item.appendChild( bar );
		item.appendChild( label );
		list.appendChild( item );
	} );

	nav.appendChild( top );
	nav.appendChild( list );

	return nav;
}

/**
 * D1 (0.9.41-rc3): "Monthly", "Weekly"… as a badge beside an amount; its colors come from Settings › Appearance, never red.
 *
 * @param {string} words "Monthly", "Weekly"…
 * @return {HTMLElement}
 */
export function frequencyBadge( words ) {
	const badge = document.createElement( 'span' );

	badge.className = 'anudan-frequency-badge';
	badge.setAttribute( 'data-anudan-frequency-badge', '' );
	badge.textContent = words;

	return badge;
}

/**
 * Shows the total beside "Step 1 of 3": "Total $25.00 [Monthly]", or nothing while there is no total.
 *
 * @param {HTMLElement} bar    From {@see buildStitch}.
 * @param {string}      amount The total, formatted, or '' for none.
 * @param {string}      every  "Monthly", "Weekly"… for a recurring gift, or ''.
 * @return {void}
 */
export function setStepTotal( bar, amount, every ) {
	const total = bar && bar.querySelector( '[data-anudan-progress-total]' );

	if ( ! total ) {
		return;
	}

	total.textContent = '';
	total.hidden = '' === amount;

	if ( '' === amount ) {
		return;
	}

	const figure = document.createElement( 'strong' );

	figure.textContent = amount;
	total.appendChild( document.createTextNode( `${ __( 'Total' ) } ` ) );
	total.appendChild( figure );

	if ( '' !== every ) {
		total.appendChild( document.createTextNode( ' ' ) );
		total.appendChild( frequencyBadge( every ) );
	}
}

/**
 * Marks the steps done up to the current one, the current one current, and announces the change.
 *
 * @param {HTMLElement} bar         From {@see buildStitch}.
 * @param {HTMLElement} liveRegion
 * @param {number}      currentStep 1 to 3.
 * @param {number}      totalSteps  3.
 * @param {boolean}     complete    Whether the donation itself completed: every step is then done.
 * @return {void}
 */
export function updateStitch( bar, liveRegion, currentStep, totalSteps, complete ) {
	const template = __( 'Step %1$d of %2$d' );
	const said = template.replace( '%1$d', String( currentStep ) ).replace( '%2$d', String( totalSteps ) );
	const count = bar.querySelector( '[data-anudan-progress-count]' );

	if ( count ) {
		count.textContent = said;
	}

	bar.querySelectorAll( '[data-anudan-stitch-segment]' ).forEach( ( item ) => {
		const index = Number( item.getAttribute( 'data-anudan-stitch-segment' ) );
		const done = complete || index < currentStep - 1;
		const current = ! complete && index === currentStep - 1;
		const mark = item.querySelector( '.anudan-checkout__progress-mark' );

		item.classList.toggle( 'anudan-checkout__stitch-segment--complete', done || current );
		item.classList.toggle( 'is-done', done );
		item.classList.toggle( 'is-current', current );

		if ( current ) {
			item.setAttribute( 'aria-current', 'step' );
		} else {
			item.removeAttribute( 'aria-current' );
		}

		if ( mark ) {
			mark.textContent = done ? '✓' : String( index + 1 );
		}
	} );

	if ( ! liveRegion ) {
		return;
	}

	liveRegion.textContent = complete ? __( 'Donation complete.' ) : said;
}
